import React, { useState, useEffect, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { importConfigAPI } from '../services/importConfigAPI';

import useDebounce from '../hooks/useDebounce';
import { useImport } from '../context/ImportContext';

const CustomSearchDropdown = ({
  placeholder = 'Digite para buscar...',
  apiEndpoint = 'listCompanies',
  baseId,
  onSelect,
  initialValue = '',
  optionLabel = 'name',
  optionValue = 'id',
  className = 'w-full',
  emptyMessage = 'Nenhum resultado encontrado',
  clearSignal
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  const isSelectedRef = useRef(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const debouncedValue = useDebounce(inputValue, 500);
  const { baseId: contextBaseId } = useImport();

  useEffect(() => {
    const fetchData = async () => {
      if (isSelectedRef.current) {
        isSelectedRef.current = false;
        return;
      }

      if (debouncedValue && debouncedValue.length >= 3) {
        setLoading(true);
        try {
          const currentBaseId = baseId || contextBaseId;
          const response = await importConfigAPI.listCompanies(debouncedValue);
          const results = response?.data || [];
          setSearchResults(results);
          setShowDropdown(true);
        } catch (error) {
          console.error('Erro na busca:', error);
          setSearchResults([]);
        } finally {
          setLoading(false);
        }
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    };

    fetchData();
  }, [debouncedValue, apiEndpoint, baseId, contextBaseId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (clearSignal === undefined) return;

    // Limpa visualmente e internamente o dropdown quando o pai solicitar.
    isSelectedRef.current = false;
    setInputValue('');
    setSearchResults([]);
    setShowDropdown(false);

    // Notifica o pai (opcional), para manter consistência.
    if (onSelect) onSelect(null);

  }, [clearSignal]);


  const handleSelect = (item) => {
    isSelectedRef.current = true;
    setInputValue(item[optionLabel]);
    setShowDropdown(false);
    setSearchResults([]);
    if (onSelect) onSelect(item);
  };

  const handleInputChange = (e) => {
    isSelectedRef.current = false;
    setInputValue(e.target.value);
  };

  const handleClear = (e) => {
    e.stopPropagation(); // Evita que o input receba foco indesejado
    isSelectedRef.current = false;
    setInputValue('');
    setSearchResults([]);
    setShowDropdown(false);
    if (onSelect) onSelect(null);
    inputRef.current.focus(); // Devolve o foco ao input após limpar
  };


  return (
    <div className={`relative ${className}`}>
      <div className="p-inputgroup w-full relative">
        <InputText
          ref={inputRef}
          value={inputValue || ''}
          onChange={handleInputChange}
          onFocus={() => inputValue.length >= 3 && searchResults.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className="w-full pr-7" // Espaço para os ícones
          autoComplete="off"
        />

        {/* Container de Ícones à Direita */}
        <div 
          className="absolute flex align-items-center gap-2" 
          style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }}
        >
          {loading ? (
            <i className="pi pi-spin pi-spinner text-gray-400" />
          ) : (
            <>
              {inputValue && (
                <i 
                  className="pi pi-times cursor-pointer text-gray-400 hover:text-gray-600" 
                  onClick={handleClear}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Dropdown de Resultados */}
      {showDropdown && (
        <ul
          ref={dropdownRef}
          className="absolute z-50 w-full bg-white border border-gray-300 shadow-lg rounded-md mt-1 overflow-y-auto"
          style={{ maxHeight: '250px', listStyle: 'none', padding: 0, margin: 0 }}
        >
          {searchResults.length > 0 ? (
            searchResults.map((item, index) => (
              <li
                key={`${item[optionValue]}-${index}`}
                className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 flex justify-between items-center"
                onClick={() => handleSelect(item)}
              >
                <div className="flex flex-column text-left">
                  <span className="font-semibold text-gray-2400">{item[optionLabel]}</span>
                </div>
                <i className="pi pi-chevron-right text-gray-300 text-xs" />
              </li>
            ))
          ) : !loading && inputValue.length >= 3 ? (
            <li className="p-3 text-center text-gray-500">{emptyMessage}</li>
          ) : null}
        </ul>
      )}

      {/* Feedback de caracteres faltantes */}
      {inputValue.length > 0 && inputValue.length < 3 && !isSelectedRef.current && (
        <small className="absolute mt-1 text-orange-500" style={{ fontSize: '0.75rem' }}>
          Digite mais {3 - inputValue.length} caracteres...
        </small>
      )}
    </div>
  );
};

export default CustomSearchDropdown;